import {
  GetStaticPaths,
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticProps,
} from 'next';
import {
  TypePage,
  TypePlayKitPage,
  TypePlayKitsLandingPage,
  TypeProduct,
  TypeProductFields,
  TypeProductLandingPage,
  TypeProductPage,
  TypeProductPageFields,
} from '@/core/generated-types';
import {
  defaultCacheRevalidation,
  shopifyProductField,
} from '@/core/contentfulConfig';
import {
  CONTENT_TYPE_PAGE,
  CONTENT_TYPE_PRODUCT_PAGE,
  COURSE_URLS,
  getContentType,
} from '@/core/constants';
import { ShopifyProduct, getProductById } from '@/lib/shopify';
import { useYotpoScript } from '@/hooks/useYotpoScript';
import { loadLayout } from '@/utils/loadLayout';
import getPathsFromEntries from '@/utils/pathBuilder';
import {
  ProductPageLayout,
  PlayKitPageLayout,
  PlayKitsLandingPageLayout,
  ProductLandingPageLayout,
  Errors,
} from '@/components';
import {
  getEntriesByPageType,
  getInventoryStatusCopy,
  InventoryStatusCopy,
  ProductVariantCopy,
  ProductPageGeneralCopy,
  getProductVariantsCopy,
  getProductPageGeneralCopy,
  ContentfulPreviewData,
} from '@/lib/contentful';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import fetchShopifyProducts from '@/lib/middleware/fetchShopifyProducts';
import { ReviewTotals, getReviewTotals } from '@/lib/yotpo';
import React from 'react';
import { StartingKit } from '@/hooks/useStartingKit';
import getStartingProduct from '@/lib/api/orchestration_subflow/api/getStartingProduct';

export interface ProductPageProps {
  error: string;
  page: TypePlayKitPage | TypePlayKitsLandingPage | TypeProductPage;
  shopifyProductAddOns: { [title: string]: ShopifyProduct | null };
  shopifyAdditionalProducts: ShopifyProduct[];
  inventoryStatusCopy: InventoryStatusCopy | null;
  productVariantsCopy: ProductVariantCopy | null;
  productPageGeneralCopy: ProductPageGeneralCopy | null;
  yotpoReviews?: ReviewTotals;
  theThinkerPlayKit: StartingKit;
  v3Translations: TypePage;
}

const ProductPage = ({
  error,
  page,
  shopifyAdditionalProducts,
  shopifyProductAddOns,
  inventoryStatusCopy,
  productVariantsCopy,
  productPageGeneralCopy,
  yotpoReviews,
  theThinkerPlayKit,
  v3Translations,
}: ProductPageProps) => {
  useYotpoScript();

  const productFields = page?.fields?.product?.fields as any;
  const shopifyProduct = productFields?.shopifyProduct as ShopifyProduct;

  const { errors, heading } = findApplicableErrorDetails([]);

  if (error) {
    return <Errors errors={errors?.concat(error)} heading={heading} />;
  }

  switch (getContentType(page)) {
    case CONTENT_TYPE_PRODUCT_PAGE.PLAY_KITS_LANDING_PAGE:
      return (
        <PlayKitsLandingPageLayout {...(page as TypePlayKitsLandingPage)} />
      );
    case CONTENT_TYPE_PRODUCT_PAGE.PLAY_KIT_PAGE:
      return (
        <>
          <PlayKitPageLayout
            shopifyProduct={shopifyProduct}
            yotpoReviews={yotpoReviews}
            theThinkerPlayKit={theThinkerPlayKit}
            v3Translations={v3Translations}
            {...(page as TypePlayKitPage)}
          />
        </>
      );
    case CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_PAGE:
      return (
        <ProductPageLayout
          shopifyProduct={shopifyProduct}
          shopifyAdditionalProducts={shopifyAdditionalProducts}
          shopifyProductAddOns={shopifyProductAddOns}
          inventoryStatusCopy={inventoryStatusCopy}
          productVariantsCopy={productVariantsCopy}
          productPageGeneralCopy={productPageGeneralCopy}
          yotpoReviews={yotpoReviews}
          {...(page as TypeProductPage)}
        />
      );

    case CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_LANDING_PAGE:
      return <ProductLandingPageLayout {...(page as TypeProductLandingPage)} />;

    default:
      console.warn('No matching product page layout');
      return null;
  }
};

export const getStaticPaths: GetStaticPaths =
  async (): Promise<GetStaticPathsResult> => {
    // [slug].tsx file accepts 3 different page types: Product Page, Play Kit Page, Play Kit Landing Page
    // they all share the same /products/slug route, thus we need to retrieve all possible routes for getStaticPaths.
    const pageTypes = CONTENT_TYPE_PRODUCT_PAGE;
    const getEntries = await getEntriesByPageType({ include: 1 }, pageTypes);
    let paths = getPathsFromEntries(getEntries);

    if (
      !process.env.NEXT_PUBLIC_COURSES_ENABLED ||
      process.env.NEXT_PUBLIC_COURSES_ENABLED === 'false'
    ) {
      // temporarily disable these pages https://lovevery.atlassian.net/browse/GROW-8928
      paths = paths.filter(
        (path) => COURSE_URLS.indexOf(path.params.slug || '') === -1,
      );
    }

    return {
      paths,
      fallback: false,
    };
  };

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  previewData,
}: GetStaticPropsContext) => {
  const slug = params?.slug as string;
  const locale = params?.locale as string;
  const pageTypes = CONTENT_TYPE_PRODUCT_PAGE;
  const pagesByTypes = await getEntriesByPageType(
    {
      slug,
      locale,
    },
    pageTypes,
    preview,
    previewData as ContentfulPreviewData,
  );

  const { header, footer, footerLegal } = await loadLayout({ locale });

  const v3Translations = await getEntriesByPageType(
    {
      contentGroup: 'subflow-v3',
      locale,
    },
    CONTENT_TYPE_PAGE,
    preview,
    previewData as ContentfulPreviewData,
  );

  if (v3Translations?.[0].items?.length === 0) {
    return {
      notFound: true,
    };
  }

  let page = null;

  // Finds the first page type that has an item in it.
  for (const pagesByType of pagesByTypes) {
    if (pagesByType.items.length > 0) {
      page = pagesByType;
      break;
    }
  }

  if (!page || page.items?.length === 0) {
    return {
      notFound: true,
    };
  }

  if (page) {
    page = await fetchShopifyProducts(page);
  }

  const contentfulPage = page.items?.[0];
  const fieldName = shopifyProductField();

  let inventoryStatusCopy = null;
  let productVariantsCopy = null;
  let productPageGeneralCopy = null;

  if (
    getContentType(contentfulPage) === CONTENT_TYPE_PRODUCT_PAGE.PRODUCT_PAGE
  ) {
    inventoryStatusCopy = await getInventoryStatusCopy(locale, preview);
    productVariantsCopy = await getProductVariantsCopy(locale, preview);
    productPageGeneralCopy = await getProductPageGeneralCopy(locale, preview);
  }

  const shopifyAdditionalProducts = [] as ShopifyProduct[];

  let yotpoReviews: ReviewTotals | undefined = undefined;
  const shopifyProductAddOns: { [title: string]: ShopifyProduct | null } = {};
  const contentfulPageFields = contentfulPage?.fields as TypeProductPageFields;

  if ('product' in contentfulPage?.fields) {
    const productFields = contentfulPageFields.product.fields;
    const productId = productFields[
      fieldName as keyof TypeProductFields
    ] as string;

    if (productId) {
      yotpoReviews = await getReviewTotals(
        contentfulPage?.fields.product as TypeProduct,
        locale,
      );
    }

    if (productFields.productAddOns) {
      await Promise.all(
        productFields.productAddOns.map(async (productAddOn) => {
          const productAddOnId = productAddOn.fields[
            fieldName as keyof TypeProductFields
          ] as string;

          if (productAddOnId) {
            const addOn = await getProductById(productAddOnId, locale);
            if (addOn) {
              shopifyProductAddOns[addOn.title] = addOn;
            }
          }
        }),
      );
    }
  }

  if ('additionalProducts' in contentfulPageFields) {
    const additionalProducts = contentfulPageFields.additionalProducts;

    if (additionalProducts) {
      await Promise.all(
        additionalProducts.map(async (product) => {
          const productId = product.fields[
            fieldName as keyof TypeProductFields
          ] as string;

          if (productId) {
            const product = await getProductById(productId, locale);
            if (product) {
              shopifyAdditionalProducts.push(product);
            }
          }
        }),
      );
    }
  }

  let theThinkerPlayKit = null;

  if (slug.includes('the-thinker')) {
    //  Calculates the date it was 360 days ago (so orchestration_subflow endpoint returns The Thinker PlayKit) | YYYY-MM-DD
    const aYearAgoDate = new Date(Date.now() - 360 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);

    // This is used to retrieve customizable options for The Thinker
    theThinkerPlayKit = await getStartingProduct({
      params: `date_of_birth=${aYearAgoDate}&child_name=&selected_language=en&language=en&currency_code=usd`,
    });
  }

  return {
    props: {
      shopifyProductAddOns,
      shopifyAdditionalProducts,
      page: contentfulPage || null,
      header,
      footer,
      footerLegal,
      inventoryStatusCopy,
      productVariantsCopy,
      productPageGeneralCopy,
      yotpoReviews: yotpoReviews || null,
      //  Used for customization data
      theThinkerPlayKit,
      v3Translations: v3Translations?.[0].items?.[0] || null,
    },
    revalidate: defaultCacheRevalidation,
  };
};

export default ProductPage;
